import type { HistoryKind, NotificationKind } from '@contracts/activity';
import type { AuditModule } from '@contracts/common';
import type { InvitationState, UserStatus } from '@contracts/identity';
import type { PartnerKind } from '@contracts/partners';
import type { MetricDirection } from '@contracts/strategy';
import type { Priority, UploadState } from '@contracts/tasks';

/**
 * The product's word for every closed set of values the server sends. There is one table per set, typed over
 * the contract's own union, so a value added on the server does not compile here until it has a label — and
 * every screen reads the same table, so a status cannot be "Đang làm" on one page and something else on the
 * next. A value outside its table (an old row, a code renamed since) is shown exactly as it came, never as a
 * blank: a raw code is ugly, an empty cell is a lie.
 */
export function labelOf<K extends string>(labels: Readonly<Record<K, string>>, code: string): string {
  return entryOf(labels, code, code);
}

/**
 * A code's entry in any other table keyed by the server's codes — a tone, an icon — or `fallback` for a code the
 * table does not have. The fallback is the neutral choice (the quiet tone, a plain icon): a code nobody has
 * styled yet is still drawn, never left as an empty badge or a missing component.
 */
export function entryOf<K extends string, V>(table: Readonly<Record<K, V>>, code: string, fallback: V): V {
  // `hasOwn`, not `in`: a code that happens to be called `constructor` must not find a function here.
  return Object.hasOwn(table, code) ? table[code as K] : fallback;
}

/** Shared with the server (Hana's prompt speaks the same words). */
export { TASK_STATUS_LABEL } from '@contracts/tasks';

export const PRIORITY_LABEL: Record<Priority, string> = {
  low: 'Thấp', normal: 'Bình thường', high: 'Cao', urgent: 'Khẩn cấp',
};

/** A partner of the directory: an organisation or one person (the mockup's "Tổ chức" / "Cá nhân"). */
export const PARTNER_KIND_LABEL: Record<PartnerKind, string> = { organization: 'Tổ chức', individual: 'Cá nhân' };

/** The kind of an outside recipient, as audit rows written before partners (projects spec §4.2) still say it. */
/** An outside party's kind (old rows say `person`), and a partner's (`individual`), as one table. */
export const PARTY_KIND_LABEL: Record<'person' | 'organization' | 'individual', string> = {
  person: 'Cá nhân', organization: 'Tổ chức', individual: 'Cá nhân',
};

/** A partner's labels (projects & stakeholders spec §1). */
export const PARTNER_LABEL_LABEL: Record<'investor' | 'consultant' | 'implementer' | 'supplier' | 'other', string> = {
  investor: 'Chủ đầu tư', consultant: 'Tư vấn', implementer: 'Triển khai', supplier: 'Nhà cung cấp', other: 'Khác',
};

/** A recipient is always a BOD member now; `external` stays for the audit rows written before (spec §4.2). */
export const RECIPIENT_KIND_LABEL: Record<'user' | 'external', string> = {
  user: 'Thành viên BOD', external: 'Ngoài công ty',
};

/**
 * What a history is about; also the words for a strategy link's and a metric's parent (`objective`/`priority`).
 * `external-party` is retired but still names old rows; `project` names a work item's project parent.
 */
export const HISTORY_KIND_LABEL: Record<HistoryKind | 'external-party' | 'project', string> = {
  item: 'Thẻ', 'external-party': 'Người nhận ngoài công ty', objective: 'Mục tiêu năm',
  priority: 'Ưu tiên quý', metric: 'Chỉ số', review: 'Đánh giá tháng', comment: 'Bình luận',
  attachment: 'Tệp đính kèm', 'upload-session': 'Phiên tải tệp', user: 'Tài khoản', partner: 'Đối tác',
  project: 'Dự án',
};

/** What an inbox entry tells its reader about (spec §10). */
/** D174: a background run's kind says the reader's own persona, as its notice line does (C-F6). */
export const runKindLabel = (persona: string): string => `Việc nền của ${persona}`;

export const NOTIFICATION_KIND_LABEL: Record<NotificationKind, string> = {
  assignment: 'Được giao việc', blocked: 'Việc đang vướng', due_soon: 'Sắp đến hạn', overdue: 'Quá hạn',
  assistant_pending: 'Chờ xác nhận', nudge: 'Lời nhắc',
  ext_connection_expired: 'Kết nối hết hạn', ext_tools_review: 'Tool chờ duyệt', assistant_run: runKindLabel('trợ lý'),
  onboarding_reminder: 'Cài đặt trợ lý',
  delegation_response: 'Phản hồi giao việc', delegation_decision: 'Quyết định lùi hạn', comment: 'Bình luận',
};

export const UPLOAD_STATE_LABEL: Record<UploadState, string> = {
  pending: 'Đang chờ tệp', verified: 'Đã kiểm tra', attached: 'Đã đính kèm', expired: 'Hết hạn',
};

/** Where a delegation stands (AI S9 §5.2), as its block and its audit rows say it; one table with Hana's prompt. */
export { DELEGATION_STATE_LABEL } from '@contracts/delegation';

export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  pending: 'Chờ nhận lời mời', active: 'Đang hoạt động', disabled: 'Đã vô hiệu hóa',
};

export const INVITATION_STATUS_LABEL: Record<InvitationState['status'], string> = {
  pending: 'Chờ nhận lời mời', accepted: 'Đã nhận lời mời', expired: 'Hết hạn', revoked: 'Đã thu hồi',
};

/** A password-reset link. The server's own type is not in the shared contract; the guard test holds it to it. */
export type ResetStatus = 'pending' | 'used' | 'expired' | 'revoked';
export const RESET_STATUS_LABEL: Record<ResetStatus, string> = {
  pending: 'Chưa dùng', used: 'Đã dùng', expired: 'Hết hạn', revoked: 'Đã thu hồi',
};

export const DIRECTION_LABEL: Record<MetricDirection, string> = { higher: 'Cao hơn tốt', lower: 'Thấp hơn tốt' };

/** Which part of the company wrote an audit row, or which part a scope opens. */
export const MODULE_LABEL: Record<AuditModule, string> = {
  identity: 'Tài khoản', tasks: 'Công việc', workplace: 'Workplace', strategy: 'Chiến lược', finance: 'Tài chính',
  partners: 'Đối tác', projects: 'Dự án', ai: 'Trợ lý AI',
};

/** The file kinds the server accepts, by media type. A declared `;charset=…` does not change the kind. */
export const MEDIA_TYPE_LABEL: Readonly<Record<string, string>> = {
  'application/pdf': 'Tài liệu PDF',
  'image/png': 'Ảnh PNG',
  'image/jpeg': 'Ảnh JPEG',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Tài liệu Word',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Bảng tính Excel',
  'text/plain': 'Văn bản',
  'text/csv': 'Bảng CSV',
};

export const mediaTypeLabel = (type: string): string => {
  const base = type.split(';')[0]!.trim().toLowerCase();
  return Object.hasOwn(MEDIA_TYPE_LABEL, base) ? MEDIA_TYPE_LABEL[base]! : type;
};
